import {
	faBars,
	faCheck,
	faCrosshairs,
	faFileImport,
	faPlus,
	faSortAlphaDown,
	faSync,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
} from '@mui/material'
import { useState } from 'react'

import { StructureObjectDto } from '@/endpoints/models'
import { useAppContext, useAuthContext } from '@/hooks'

import { AddModal } from '../../../AddModal'
import { ImportStructureModal } from '../ImportStructureModal'

type HeaderBurgerMenuProps = {
	alphabet: boolean
	canReveal?: boolean
	isFullTreeLoading?: boolean
	isRevealing?: boolean
	onAlphabetChange: () => void
	onRefresh: () => void
	onRevealInTree?: () => void
	onSelectModeChange: () => void
	refreshing: boolean
	selectMode: boolean
}

export const HeaderBurgerMenu = ({
	alphabet,
	canReveal,
	isFullTreeLoading,
	isRevealing,
	onAlphabetChange,
	onRefresh,
	onRevealInTree,
	onSelectModeChange,
	refreshing,
	selectMode,
}: HeaderBurgerMenuProps) => {
	const { t } = useAppContext()
	const auth = useAuthContext()
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
	const [addModalOpen, setAddModalOpen] = useState(false)
	const [importModalOpen, setImportModalOpen] = useState(false)

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setMenuAnchor(event.currentTarget)
	}

	const handleMenuClose = () => {
		setMenuAnchor(null)
	}

	const handleOpenAddModal = () => {
		handleMenuClose()
		setAddModalOpen(true)
	}

	const handleOpenImportModal = () => {
		handleMenuClose()
		setImportModalOpen(true)
	}

	return (
		<>
			<IconButton
				data-testid="tree-panel-menu"
				onClick={handleMenuOpen}
				size="small"
				sx={{ ml: 'auto', color: 'inherit' }}
			>
				<FontAwesomeIcon icon={faBars} />
			</IconButton>
			<Menu
				anchorEl={menuAnchor}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				onClose={handleMenuClose}
				open={Boolean(menuAnchor)}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				slotProps={{
					list: { 'data-testid': 'tree-panel-menu-list' },
				}}
			>
				<MenuItem
					data-testid="tree-panel-menu-select-mode"
					onClick={() => {
						onSelectModeChange()
						handleMenuClose()
					}}
					selected={selectMode}
				>
					<ListItemIcon>
						<FontAwesomeIcon icon={faCheck} />
					</ListItemIcon>
					<ListItemText>{t('SELECT_MODE')}</ListItemText>
				</MenuItem>

				<MenuItem
					data-testid="tree-panel-menu-alphabet"
					onClick={() => {
						onAlphabetChange()
						handleMenuClose()
					}}
					selected={alphabet}
				>
					<ListItemIcon>
						<FontAwesomeIcon icon={faSortAlphaDown} />
					</ListItemIcon>
					<ListItemText>{t('ALPHABETICAL_ORDER')}</ListItemText>
				</MenuItem>

				{onRevealInTree && (
					<MenuItem
						data-testid="tree-panel-menu-reveal"
						disabled={!canReveal || isRevealing || isFullTreeLoading}
						onClick={() => {
							onRevealInTree()
							handleMenuClose()
						}}
					>
						<ListItemIcon>
							<FontAwesomeIcon icon={faCrosshairs} spin={isRevealing} />
						</ListItemIcon>
						<ListItemText>{t('REVEAL_IN_TREE')}</ListItemText>
					</MenuItem>
				)}

				<MenuItem
					data-testid="tree-panel-menu-refresh"
					disabled={refreshing || isFullTreeLoading}
					onClick={() => {
						if (refreshing || isFullTreeLoading) return
						onRefresh()
						handleMenuClose()
					}}
				>
					<ListItemIcon>
						<FontAwesomeIcon icon={faSync} spin={refreshing || isFullTreeLoading} />
					</ListItemIcon>
					<ListItemText>{t('REFRESH')}</ListItemText>
				</MenuItem>

				{auth.canCreateSystem() && (
					<>
						<Divider />
						<MenuItem data-testid="tree-panel-menu-create-system" onClick={handleOpenAddModal}>
							<ListItemIcon>
								<FontAwesomeIcon icon={faPlus} />
							</ListItemIcon>
							<ListItemText>{t('CREATE_SYSTEM')}</ListItemText>
						</MenuItem>
						<MenuItem data-testid="tree-panel-menu-import-system" onClick={handleOpenImportModal}>
							<ListItemIcon>
								<FontAwesomeIcon icon={faFileImport} />
							</ListItemIcon>
							<ListItemText>{t('IMPORT_SYSTEM')}</ListItemText>
						</MenuItem>
					</>
				)}
			</Menu>

			{/* Dialogs rendered outside Menu to prevent unmounting issues */}
			{addModalOpen && (
				<AddModal
					onClose={() => setAddModalOpen(false)}
					onRefresh={onRefresh}
					type={StructureObjectDto.TypeEnum.SYSTEM}
				/>
			)}
			{importModalOpen && (
				<ImportStructureModal
					onClose={() => {
						setImportModalOpen(false)
						onRefresh()
					}}
					title={t('IMPORT_SYSTEM')}
				/>
			)}
		</>
	)
}
